import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import AccountCircle from "@material-ui/icons/AccountCircle";
import OpenInNew from "@material-ui/icons/OpenInNew";
import { showNotification, CREATE } from "react-admin";
import dataProviderFactory from "../dataProvider";
import { API_ROOT } from "../api-config";
import {
  isAgencyCompleteProfileRecord,
  isFromAgencyRecord,
} from "../utils/agencyRiderDocuments";

const DEFAULT_ICON_SIZE = 24;

export const PROFILE_MARK_STATUS = {
  INCOMPLETE: "incomplete",
  MISSING_DOCS: "missing_docs",
  COMPLETE: "complete",
};

const STATUS_UI = {
  [PROFILE_MARK_STATUS.INCOMPLETE]: {
    color: "#d32f2f",
    title: "Profile information incomplete",
    dialogTitle: "Send complete-profile email?",
    dialogBody:
      "An email will be sent to the client asking them to complete their missing profile information. Do you want to continue?",
  },
  [PROFILE_MARK_STATUS.MISSING_DOCS]: {
    color: "#ed6c02",
    title: "Profile complete — missing documents",
    dialogTitle: "Send complete-profile email?",
    dialogBody:
      "Profile information is complete, but passport or driving licence files are still missing. An email will be sent asking the client to upload them. Do you want to continue?",
  },
  [PROFILE_MARK_STATUS.COMPLETE]: {
    color: "#2e7d32",
    title: "Profile complete",
    dialogTitle: "Profile complete",
    dialogBody: "",
  },
};

function sourceRecords(record) {
  const sources = [];
  if (!record) return sources;
  sources.push(record);
  const driver = record.driver;
  if (Array.isArray(driver) && driver[0]) sources.push(driver[0]);
  else if (driver) sources.push(driver);
  return sources;
}

function firstValue(sources, keys) {
  for (let i = 0; i < sources.length; i += 1) {
    const src = sources[i];
    if (!src) continue;
    for (let k = 0; k < keys.length; k += 1) {
      const value = src[keys[k]];
      if (value !== undefined && value !== null && value !== "") return value;
    }
  }
  return undefined;
}

function coerceBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return undefined;
}

function firstBoolean(sources, key) {
  for (let i = 0; i < sources.length; i += 1) {
    const src = sources[i];
    if (!src) continue;
    const coerced = coerceBoolean(src[key]);
    if (coerced !== undefined) return coerced;
  }
  return undefined;
}

function hasUploadedPath(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

export function getProfileMarkStatus(record) {
  const sources = sourceRecords(record);
  if (!sources.length) return PROFILE_MARK_STATUS.INCOMPLETE;

  const fromAgency = isFromAgencyRecord(record);
  if (fromAgency) {
    const infoFlag = firstBoolean(sources, "profile_info_complete");
    const docsFlag = firstBoolean(sources, "profile_documents_complete");
    const passport = firstValue([record], [
      "passport_image_path",
      "passportImagePath",
    ]);
    const licence = firstValue([record], [
      "driving_licence_image_path",
      "drivingLicenceImagePath",
    ]);
    const docsFromFiles =
      hasUploadedPath(passport) && hasUploadedPath(licence);
    const infoComplete = infoFlag === true;
    const docsComplete =
      typeof docsFlag === "boolean" ? docsFlag : docsFromFiles;
    if (infoComplete && docsComplete) return PROFILE_MARK_STATUS.COMPLETE;
    if (infoComplete && !docsComplete) return PROFILE_MARK_STATUS.MISSING_DOCS;
    return PROFILE_MARK_STATUS.INCOMPLETE;
  }

  const infoFlag = firstBoolean(sources, "profile_info_complete");
  const docsFlag = firstBoolean(sources, "profile_documents_complete");

  const passport = firstValue(sources, [
    "passport_image_path",
    "passportImagePath",
  ]);
  const licence = firstValue(sources, [
    "driving_licence_image_path",
    "drivingLicenceImagePath",
  ]);
  const docsFromFiles =
    hasUploadedPath(passport) && hasUploadedPath(licence);

  const infoComplete = infoFlag === true;
  const docsComplete =
    typeof docsFlag === "boolean" ? docsFlag : docsFromFiles;

  if (infoComplete && docsComplete) return PROFILE_MARK_STATUS.COMPLETE;
  if (infoComplete && !docsComplete) return PROFILE_MARK_STATUS.MISSING_DOCS;
  return PROFILE_MARK_STATUS.INCOMPLETE;
}

export const isDriverProfileComplete = (record) =>
  getProfileMarkStatus(record) === PROFILE_MARK_STATUS.COMPLETE;

class ProfileCompleteMark extends Component {
  state = { sending: false, confirmOpen: false, opening: false };

  getStatus = () => getProfileMarkStatus(this.props.record);

  resolveIds = () => {
    const { record, clientId, rentalId } = this.props;
    const resolvedClientId =
      clientId ||
      (record &&
        (record.client_id ||
          (record.driver && record.driver.id) ||
          (record.id && !record.booking_id ? record.id : null)));
    const resolvedRentalId =
      rentalId || (record && record.booking_id ? record.id : null);
    return { resolvedClientId, resolvedRentalId };
  };

  isAgencyRecord = () => isAgencyCompleteProfileRecord(this.props.record);

  handleClick = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.isAgencyRecord()) return;
    if (this.getStatus() === PROFILE_MARK_STATUS.COMPLETE || this.state.sending) {
      return;
    }
    this.setState({ confirmOpen: true });
  };

  handleOpenForm = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.isAgencyRecord()) return;
    if (this.state.opening) return;

    const { resolvedClientId, resolvedRentalId } = this.resolveIds();
    if (!resolvedClientId && !resolvedRentalId) {
      this.props.showNotification("No client found for this record", "warning");
      return;
    }

    try {
      this.setState({ opening: true });
      const params = [];
      if (resolvedClientId) params.push(`clientId=${encodeURIComponent(resolvedClientId)}`);
      if (resolvedRentalId) params.push(`rentalId=${encodeURIComponent(resolvedRentalId)}`);
      const response = await fetch(`${API_ROOT}/complete_profile/link?${params.join("&")}`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.url) {
        throw new Error(data.message || "Could not open complete-info form");
      }
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error opening complete-info form:", error);
      this.props.showNotification(
        "Error: could not open complete-info form",
        "warning"
      );
    } finally {
      this.setState({ opening: false });
    }
  };

  handleCancel = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.state.sending) return;
    this.setState({ confirmOpen: false });
  };

  handleConfirm = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.isAgencyRecord()) return;
    if (this.getStatus() === PROFILE_MARK_STATUS.COMPLETE || this.state.sending) {
      return;
    }

    try {
      this.setState({ sending: true });
      const dataProvider = await dataProviderFactory("rest");
      const { showNotification } = this.props;
      const { resolvedClientId, resolvedRentalId } = this.resolveIds();

      const body = {};
      if (resolvedClientId) body.clientId = resolvedClientId;
      if (resolvedRentalId) body.rentalId = resolvedRentalId;
      if (!resolvedClientId && !resolvedRentalId) {
        showNotification("No client found for this record", "warning");
        this.setState({ sending: false });
        return;
      }

      const result = await dataProvider(CREATE, "complete_profile/send", {
        data: body,
      });

      const email =
        (result && result.data && result.data.email) ||
        (result && result.email) ||
        "";
      showNotification(
        email
          ? `Complete-info email sent to ${email}`
          : "Complete-info email sent",
        "info"
      );
    } catch (error) {
      console.error("Error sending complete profile email:", error);
      this.props.showNotification(
        "Error: could not send complete-info email",
        "warning"
      );
    } finally {
      this.setState({ sending: false, confirmOpen: false });
    }
  };

  render() {
    const { record } = this.props;
    if (record && Number(record.rental_status) === 3) return null;

    const status = this.getStatus();
    const ui = STATUS_UI[status] || STATUS_UI[PROFILE_MARK_STATUS.INCOMPLETE];
    const size = this.props.size || DEFAULT_ICON_SIZE;
    const isAgency = this.isAgencyRecord();
    const isComplete = status === PROFILE_MARK_STATUS.COMPLETE;
    const disabled = isAgency || isComplete || this.state.sending;
    const iconBox = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      lineHeight: 0,
    };
    const tooltipTitle = isAgency
      ? `${ui.title} (agency — email/link disabled)`
      : ui.title;

    return (
      <Fragment>
        <span style={{ ...iconBox, gap: 10, margin: "0 4px" }}>
          <Tooltip title={tooltipTitle}>
            <span
              onClick={isAgency ? undefined : this.handleClick}
              role={isAgency ? undefined : "button"}
              tabIndex={disabled ? -1 : 0}
              aria-label={tooltipTitle}
              onKeyDown={
                isAgency
                  ? undefined
                  : (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        this.handleClick(event);
                      }
                    }
              }
              style={{
                ...iconBox,
                cursor: disabled ? "default" : "pointer",
                opacity: this.state.sending ? 0.6 : 1,
              }}
            >
              <AccountCircle
                style={{
                  fontSize: size,
                  width: size,
                  height: size,
                  color: ui.color,
                }}
              />
            </span>
          </Tooltip>
          {isAgency ? null : (
          <Tooltip title="Open complete-info form">
            <span
              onClick={this.handleOpenForm}
              role="button"
              tabIndex={this.state.opening ? -1 : 0}
              aria-label="Open complete-info form"
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  this.handleOpenForm(event);
                }
              }}
              style={{
                ...iconBox,
                cursor: this.state.opening ? "wait" : "pointer",
                opacity: this.state.opening ? 0.6 : 1,
              }}
            >
              <OpenInNew
                style={{
                  fontSize: size,
                  width: size,
                  height: size,
                  color: "#555",
                }}
              />
            </span>
          </Tooltip>
          )}
        </span>
        <Dialog
          open={!isAgency && this.state.confirmOpen}
          onClose={this.handleCancel}
          onClick={(event) => event.stopPropagation()}
          aria-labelledby="complete-profile-confirm-title"
        >
          <DialogTitle id="complete-profile-confirm-title">
            {ui.dialogTitle}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{ui.dialogBody}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCancel} disabled={this.state.sending}>
              Cancel
            </Button>
            <Button
              onClick={this.handleConfirm}
              color="primary"
              variant="contained"
              disabled={this.state.sending}
            >
              {this.state.sending ? "Sending..." : "Send email"}
            </Button>
          </DialogActions>
        </Dialog>
      </Fragment>
    );
  }
}

ProfileCompleteMark.propTypes = {
  record: PropTypes.object,
  complete: PropTypes.bool,
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rentalId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  size: PropTypes.number,
  showNotification: PropTypes.func,
};

export default connect(null, {
  showNotification,
})(ProfileCompleteMark);



// WEBPACK FOOTER //
// ./src/components/profileCompleteMark.js